import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, ButtonGroup, ClickAwayListener, styled } from '@mui/material';
import clsx from 'clsx';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { API_BASE_URLS, API_ROUTES, osType } from '@/constants';
import { useAuth, useCDFIData } from '@/hooks';
import { downloadFile } from '@/utils';

import { DownloadButtonStateProps, DownloadStatusEnum } from './types';

import styles from './styles.module.scss';

import KeyboardArrowDownIcon from '@/assets/icons/arrow-down.svg?react';
import DoneIcon from '@/assets/icons/done.svg?react';
import DownloadIcon from '@/assets/icons/download.svg?react';
import FileIcon from '@/assets/icons/file.svg?react';

const DataPreparerButton = styled(Button)({
  width: '234px',
  fontFamily: 'Open Sans',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  display: 'flex',
  alignItems: 'center',
  letterSpacing: '0.2px',
  height: '40px',
  borderRadius: '4px',
  padding: '10px',
  background: '#52B36B',
  color: '#fff',
  '&:hover': {
    background: '#52B36B',
    opacity: 0.8,
    filter: 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))',
  },
});

const StyledUploadModalButtonGroup = styled(ButtonGroup)({
  width: '100%',
  boxShadow: 'none',
  height: '40px',
  fontSize: '14px',
  fontWeight: 600,
  '& .MuiButton-root:first-of-type': {
    width: '100%',
  },
  '& .MuiButton-root:last-of-type': {
    minWidth: 72,
  },
});

const StyledListButton = styled(Button)({
  fontSize: 14,
  fontWeight: 600,
  borderRadius: '4px',
  color: '#fff',
  background: '#2B39B9',
  '&:hover': {
    background: 'rgba(43, 57, 185, 0.8)',
  },
});

const CDFIExportButtonGroup = styled(ButtonGroup)({
  boxShadow: 'none',
  height: '48px',
  width: '212px',
  gap: '2px',
  background: '#fff',
  '& .MuiButton-root': {
    border: '1px solid #2B39B9',
    '&:hover': {
      background: '#2B39B9',
      border: '1px solid #2B39B9',
    },
  },
  '& .MuiButton-root:first-of-type': {
    width: '100%',
  },
  '& .MuiButton-root:last-of-type': {
    minWidth: '52px',
  },
});

const DOWNLOAD_LINKS = {
  dataPreparer: (os?: string) => API_ROUTES.DATA_PREPARER_DOWNLOAD + os,
  crossSellList: API_ROUTES.CROSS_SELL_LIST,
  preQualificationData: API_ROUTES.PRE_QUALIFICATION_DATA,
  tmAnalysisDetails: API_ROUTES.CDFI_TM_ANALYSIS_DETAILS,
  zipIAOverlapAnalysis: API_ROUTES.ZIP_IA_OVERLAP_ANALYSIS,
  fsOptionAnalysisDetails: API_ROUTES.FS_OPTION_ANALYSIS_DETAILS,
};

const cdfiProviderLinks: string[] = [
  DOWNLOAD_LINKS.crossSellList,
  DOWNLOAD_LINKS.preQualificationData,
  DOWNLOAD_LINKS.tmAnalysisDetails,
  DOWNLOAD_LINKS.zipIAOverlapAnalysis,
  DOWNLOAD_LINKS.fsOptionAnalysisDetails,
];

export const DownloadButtonComponent: FC<DownloadButtonStateProps> = ({
  type = 'dataPreparer',
  os,
  onSuccess,
  preQualReportParams,
}) => {
  const { statsParams, cdfiStatsData } = useCDFIData();
  const token = useAuth().user?.access_token;
  const switchRef = useRef(null);
  const dataVersionRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<DownloadStatusEnum>(DownloadStatusEnum.IDLE);

  const isCDFIExportButton = type === 'cdfiReport' || type === 'cdfiCertification';

  const handleSuccessDownload = useCallback(() => {
    onSuccess?.();
    setStatus(DownloadStatusEnum.DONE);
    setTimeout(handleClose, 2000);
  }, [onSuccess]);

  const handleDownload = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const linkUrl = e.currentTarget.getAttribute('data-url') ?? '';

      setProgress(0);
      setStatus(DownloadStatusEnum.IN_PROGRESS);

      let provider = API_BASE_URLS.BASE_URL;

      if (cdfiProviderLinks.includes(linkUrl.split('?')[0])) {
        provider = API_BASE_URLS.CDFI_API_URL;
      }

      void downloadFile({
        token: token ?? '',
        isNewTab: true,
        linkUrl,
        callbackFunction: handleSuccessDownload,
        progressFunction: setProgress,
        baseApiUrl: provider,
      });
    },
    [token, os, handleSuccessDownload],
  );

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setStatus(DownloadStatusEnum.IDLE);
  }, []);

  const getIdleButton = useCallback(
    (type: DownloadButtonStateProps['type']) => {
      switch (type) {
        case 'dataPreparer':
          return (
            <DataPreparerButton data-url={DOWNLOAD_LINKS.dataPreparer(os)} onClick={handleDownload}>
              Download New Version
            </DataPreparerButton>
          );

        case 'uploadModal':
          return (
            <>
              <StyledUploadModalButtonGroup variant="contained" color="secondary" aria-label="split button">
                <StyledListButton data-url={DOWNLOAD_LINKS.dataPreparer(os)} onClick={handleDownload}>
                  Download Data Preparer for {os ? osType[os] : ''}
                </StyledListButton>

                <StyledListButton
                  color="secondary"
                  size="small"
                  aria-controls={open ? 'split-button-menu' : ''}
                  aria-expanded={open}
                  aria-label="select data preparer version"
                  aria-haspopup="menu"
                  onClick={handleToggle}
                >
                  <KeyboardArrowDownIcon color="#fff" />
                </StyledListButton>
              </StyledUploadModalButtonGroup>

              <CSSTransition in={open} classNames="my-node" timeout={100} nodeRef={dataVersionRef} unmountOnExit>
                <ClickAwayListener onClickAway={handleClose}>
                  <div className={styles.dropdown}>
                    {Object.entries(osType).map(([osKey, option]) => (
                      <span
                        data-url={DOWNLOAD_LINKS.dataPreparer(osKey)}
                        onClick={handleDownload}
                        key={option}
                        className={styles.dropdown__item}
                      >
                        Download Data Preparer for {option}
                      </span>
                    ))}
                  </div>
                </ClickAwayListener>
              </CSSTransition>
            </>
          );

        case 'cdfiReport':
          return (
            <>
              <CDFIExportButtonGroup className={styles.exportBtnWrapper}>
                <StyledListButton onClick={handleToggle}>
                  <FileIcon color="#C2CEE0" />
                  <span>Export</span>
                </StyledListButton>

                <StyledListButton
                  color="secondary"
                  size="small"
                  aria-controls={open ? 'split-button-menu' : ''}
                  aria-expanded={open}
                  aria-label="export cdfi report"
                  aria-haspopup="menu"
                  onClick={handleToggle}
                >
                  <KeyboardArrowDownIcon color="#fff" />
                </StyledListButton>
              </CDFIExportButtonGroup>

              <CSSTransition in={open} classNames="my-node" timeout={100} nodeRef={dataVersionRef} unmountOnExit>
                <ClickAwayListener onClickAway={handleClose}>
                  <div className={styles.dropdown}>
                    <span
                      data-url={DOWNLOAD_LINKS.preQualificationData + (preQualReportParams ?? '')}
                      onClick={handleDownload}
                      className={styles.dropdown__item}
                    >
                      Pre-Qualification Data
                    </span>

                    <span
                      data-url={DOWNLOAD_LINKS.crossSellList + (preQualReportParams ?? '')}
                      onClick={handleDownload}
                      className={styles.dropdown__item}
                    >
                      CDFI Cross-Sell List
                    </span>
                  </div>
                </ClickAwayListener>
              </CSSTransition>
            </>
          );

        case 'cdfiCertification':
          return (
            <>
              <CDFIExportButtonGroup className={styles.exportBtnWrapper}>
                <StyledListButton onClick={handleToggle}>
                  <FileIcon color="#C2CEE0" />
                  <span>Export</span>
                </StyledListButton>

                <StyledListButton
                  color="secondary"
                  size="small"
                  aria-controls={open ? 'split-button-menu' : ''}
                  aria-expanded={open}
                  aria-label="export cdfi certification report"
                  aria-haspopup="menu"
                  onClick={handleToggle}
                >
                  <KeyboardArrowDownIcon color="#fff" />
                </StyledListButton>
              </CDFIExportButtonGroup>

              <CSSTransition in={open} classNames="my-node" timeout={100} nodeRef={dataVersionRef} unmountOnExit>
                <ClickAwayListener onClickAway={handleClose}>
                  <div className={styles.dropdown}>
                    <span
                      data-url={DOWNLOAD_LINKS.tmAnalysisDetails + statsParams}
                      onClick={handleDownload}
                      className={styles.dropdown__item}
                    >
                      CDFI TM Analysis Details
                    </span>

                    <span
                      data-url={DOWNLOAD_LINKS.zipIAOverlapAnalysis}
                      onClick={handleDownload}
                      className={styles.dropdown__item}
                    >
                      ZIP-IA Overlap Analysis
                    </span>

                    {cdfiStatsData?.result?.accountHolders && (
                      <span
                        data-url={DOWNLOAD_LINKS.fsOptionAnalysisDetails + statsParams}
                        onClick={handleDownload}
                        className={styles.dropdown__item}
                      >
                        FS Option Analysis Details
                      </span>
                    )}
                  </div>
                </ClickAwayListener>
              </CSSTransition>
            </>
          );
      }
    },
    [open, handleDownload, dataVersionRef, handleClose, handleToggle, os, cdfiStatsData],
  );

  const buttonStates = useMemo(
    () => ({
      [DownloadStatusEnum.IDLE]: getIdleButton(type),
      [DownloadStatusEnum.IN_PROGRESS]: (
        <>
          <div
            className={clsx(
              styles.button,
              styles.button__loading,
              styles.buttonState,
              type === 'uploadModal' && styles.button__fullWidth,
              isCDFIExportButton && styles.button__cdfi,
            )}
          >
            <span className={styles.button__loading__text}>
              <span className={styles.marginRight}>
                <DownloadIcon />
              </span>

              <span>{isCDFIExportButton ? 'Processing' : 'Downloading'}</span>
            </span>
          </div>

          <div className={styles.button__loader} style={{ width: `${progress}%` }} />
        </>
      ),
      [DownloadStatusEnum.MAY_TAKE_TIME]: (
        <>
          <div
            className={clsx(
              styles.button,
              styles.button__loading,
              styles.buttonState,
              styles.button__cdfi,
              styles.button__cdfi__small_text,
            )}
          >
            <span className={styles.button__loading__text}>
              <span className={styles.marginRight}>
                <DownloadIcon />
              </span>

              <span>Exporting may take time</span>
            </span>
          </div>

          <div className={styles.button__loader} style={{ width: `${progress || 0}%` }} />
        </>
      ),
      [DownloadStatusEnum.DONE]: (
        <div
          className={clsx(
            styles.button,
            styles.button__done,
            type === 'uploadModal' && styles.button__fullWidth,
            isCDFIExportButton && styles.button__cdfi,
          )}
        >
          <span className={styles.marginRight}>
            <DoneIcon color="#fff" />
          </span>

          <span>Done</span>
        </div>
      ),
    }),
    [progress, type, getIdleButton],
  );

  useEffect(() => {
    if (isCDFIExportButton && status === DownloadStatusEnum.IN_PROGRESS && progress > 1) {
      setStatus(DownloadStatusEnum.MAY_TAKE_TIME);
    }
  }, [status, progress, type, isCDFIExportButton]);

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition timeout={100} key={status} classNames="item" nodeRef={switchRef}>
        <div ref={switchRef} className={styles.buttonsContainer}>
          {buttonStates[status]}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export const DownloadButton = memo(DownloadButtonComponent);
